import {
  Lead,
  GuideSection,
  P,
  UL,
  LI,
  Term,
  GuideLink,
  Callout,
  GuideTable,
} from './guide-ui';

/**
 * Body for "ISO 27001 logging requirements" guide.
 * Accuracy: ISO/IEC 27001:2022 Annex A controls A.8.15 (Logging),
 * A.8.16 (Monitoring activities), A.8.17 (Clock synchronisation).
 */
export function Iso27001LoggingGuideBody() {
  return (
    <>
      <Lead>
        ISO 27001 does not hand you a logging checklist with fixed numbers. It
        requires logging, monitoring and accurate time as risk-based controls in
        your information security management system (ISMS). This guide explains the
        relevant 2022 Annex A controls (A.8.15, A.8.16, A.8.17), what an auditor
        expects, and how long to keep logs.
      </Lead>

      <Callout title="Not legal advice">
        <p>
          This is general guidance to help you scope logging for ISO 27001. The
          binding requirements are the standard itself and your certification body&apos;s
          interpretation; treat your Statement of Applicability and risk assessment as
          the source of truth.
        </p>
      </Callout>

      <GuideSection id="what-is-iso27001" title="ISO 27001 and logging">
        <P>
          <Term>ISO/IEC 27001</Term> is the international standard for an information
          security management system. The 2022 revision organises its Annex A into 93
          controls across four themes; logging and monitoring sit in the{' '}
          <Term>Technological controls</Term> (the A.8 group). Rather than prescribe
          retention periods, ISO 27001 requires you to decide what to log and how long
          to keep it based on your risk assessment, and to actually use the logs.
        </P>
      </GuideSection>

      <GuideSection id="the-controls" title="The three relevant controls">
        <GuideTable
          head={['Control', 'Name', 'What it requires']}
          rows={[
            ['A.8.15', 'Logging', 'Produce, store, protect and analyse logs of relevant events, at minimum user activities such as logins, logouts and failed authentication.'],
            ['A.8.16', 'Monitoring activities', 'Monitor networks, systems and applications for anomalous behaviour and to detect potential incidents.'],
            ['A.8.17', 'Clock synchronisation', 'Synchronise the clocks of all relevant systems to an approved time source (typically NTP) so timestamps are accurate and correlatable.'],
          ]}
        />
        <P>
          The three work together: A.8.15 produces the records, A.8.16 makes sure
          someone (or something) watches them, and A.8.17 keeps timestamps accurate so
          events from different systems can be correlated during an investigation.
        </P>
      </GuideSection>

      <GuideSection id="what-to-log" title="What to log for A.8.15">
        <UL>
          <LI><Term>Authentication events</Term>: logins, logouts, and failed attempts.</LI>
          <LI><Term>Privileged and administrative activity</Term>: account and permission changes, admin actions.</LI>
          <LI><Term>System and security events</Term>: errors, security tool alerts, configuration changes.</LI>
          <LI><Term>Access to sensitive data and systems</Term>: who accessed what, and when.</LI>
        </UL>
        <P>
          Logs must be <Term>protected</Term> against tampering and unauthorised
          access, an attacker who can edit logs can erase their tracks, and they
          must be <Term>analysed</Term>, not just collected.
        </P>
      </GuideSection>

      <GuideSection id="retention" title="How long to keep logs for ISO 27001">
        <P>
          ISO 27001 sets <Term>no fixed retention period</Term>. You determine it from
          your risk assessment, legal and contractual obligations, and the need to
          investigate incidents. A commonly used baseline is at least{' '}
          <Term>12 months</Term> of logs, but the right answer is whatever your ISMS
          documents and justifies. Where other regimes also apply, take the strictest.
          See{' '}
          <GuideLink href="/guides/log-retention-requirements">log retention requirements by regulation</GuideLink>.
        </P>
      </GuideSection>

      <GuideSection id="auditor" title="What an auditor looks for">
        <UL>
          <LI>Evidence that relevant events are logged centrally and protected.</LI>
          <LI>Evidence of active monitoring and review (not just storage).</LI>
          <LI>NTP configuration on a sample of systems and clock-drift monitoring (A.8.17).</LI>
          <LI>A documented, risk-justified retention period in your ISMS.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse supports ISO 27001 logging">
        <P>
          LogPulse centralises and protects logs, monitors them in real time with
          anomaly detection and a risk-based{' '}
          <GuideLink href="/siem">SIEM</GuideLink>, and maps detections to ISO 27001
          (alongside NIS2 and DORA) controls for audit evidence. Accurate,
          synchronised timestamps are preserved on ingest, supporting A.8.17
          correlation. LogPulse supports your ISO 27001 programme and produces
          evidence; it is not itself a certification. See also{' '}
          <GuideLink href="/guides/nis2-logging-requirements">NIS2</GuideLink> and{' '}
          <GuideLink href="/guides/dora-logging-monitoring-requirements">DORA</GuideLink>{' '}
          logging.
        </P>
      </GuideSection>
    </>
  );
}
